import React, { useState } from "react";
import { Box, CircularProgress, Collapse, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AgentAvatar from "../AgentAvatar";
import useAgentStore from "../useAgentStore";
import { AVATAR_SIZE, TEXT_INDENT } from "./conversationConstants";
import { messageRowSx, getAgentAvatarClickHandler } from "./conversationUtils";
import {
  buildConversationMessages,
  renderTypedAssistantParts,
} from "./buildConversationMessages";

const EMPTY_CORRELATION_IDS = new Set();

// Persisted transcript: render the full child session with the exact same builder
// the main chat uses, so the expanded card matches the standalone agent session
// verbatim — user request bubble, assistant reasoning, tool calls and text — with
// no filtering or rewriting.
function renderPersistedChildBody(
  messages,
  childSessionId,
  agentName,
  agentId,
  onOpenAgentSettings
) {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const { rendered } = buildConversationMessages({
    messages,
    session: { chatSessionId: childSessionId },
    defaultAgentId: agentId,
    defaultAgentName: agentName,
    chatUiPolicy: {},
    onOpenAgentSettings,
  });
  return rendered;
}

// Live stream: only assistant ui_chunks are relayed while the node runs, so render
// the streamed assistant parts as they arrive (the persisted view above replaces
// this once the run settles and its transcript is fetched).
function renderLiveChildBody(messages, agentName, agentId, onOpenAgentSettings) {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  const out = [];
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (!msg || msg.role !== "assistant" || !Array.isArray(msg.parts)) continue;
    const els = renderTypedAssistantParts(
      msg,
      `team-child-${msg.messageId || msg.id || i}`,
      agentName,
      agentId,
      false,
      onOpenAgentSettings,
      null,
      null,
      true,
      true,
      true,
      EMPTY_CORRELATION_IDS,
      null,
      null,
      null,
      null,
      true,
      null,
      null
    );
    out.push(...els);
  }
  return out;
}

/**
 * Collapsible live-activity card for a single team member node. While the node is
 * running it streams the child's steps (relayed via team_child_activity) and is
 * expanded by default; when the node finishes it auto-collapses. On expand of a
 * settled card with no streamed content, the full child transcript is lazily
 * fetched from the persisted child session.
 *
 * `fallbackEntry` lets the card persist across reloads: when there is no live
 * activity in the store (cold load), the card is reconstructed from the projected
 * message's metadata (a settled "done" entry) and hydrates its body on expand.
 */
export default function TeamActivityCard({
  runId,
  fallbackEntry = null,
  grouped,
  onOpenAgentSettings,
}) {
  const liveEntry = useAgentStore((s) => s._teamActivityByRun[runId]);
  const loadChildTranscript = useAgentStore((s) => s._loadTeamChildTranscript);
  const entry = liveEntry || fallbackEntry;

  const status = entry?.status || "running";
  const [expanded, setExpanded] = useState(status === "running");
  const [prevStatus, setPrevStatus] = useState(status);
  if (status !== prevStatus) {
    // Auto-collapse once when the node settles (adjust state during render — no effect).
    setPrevStatus(status);
    if (status === "done") setExpanded(false);
  }

  if (!entry) return null;

  const { agentName, agentId, childSessionId, label, messages, persistedMessages } = entry;
  const displayName = agentName || label || "Teammate";
  const isRunning = status === "running";
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  // Prefer the persisted transcript (full session, rendered as-is). Until it's
  // fetched, show the live assistant stream.
  const bodyElements =
    Array.isArray(persistedMessages) && persistedMessages.length > 0
      ? renderPersistedChildBody(
          persistedMessages,
          childSessionId,
          displayName,
          agentId,
          onOpenAgentSettings
        )
      : renderLiveChildBody(messages, displayName, agentId, onOpenAgentSettings);

  const childHref =
    agentId && childSessionId
      ? `/agents/chats?agent=${encodeURIComponent(
          agentId
        )}&sessionId=${encodeURIComponent(childSessionId)}`
      : null;

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    // On expanding a settled card, fetch the full persisted transcript so it shows
    // the real child session as-is (the live stream only captured assistant parts).
    if (next && !isRunning && childSessionId && !persistedMessages) {
      loadChildTranscript(runId, childSessionId, fallbackEntry);
    }
  };

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar name={displayName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "260px",
            backgroundColor: "background.paper",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: "action.hover",
              cursor: "pointer",
            }}
            onClick={handleToggle}
          >
            {isRunning ? (
              <CircularProgress size={13} thickness={5} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "success.main" }} />
            )}
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{ fontSize: "12px", color: "text.secondary", flex: 1 }}
            >
              {isRunning ? "Working…" : "Done"}
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: "text.disabled",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 200ms",
              }}
            />
          </Box>

          {/* Collapsible streamed/persisted child body */}
          <Collapse in={expanded}>
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              {bodyElements.length > 0 ? (
                bodyElements
              ) : (
                <Typography sx={{ fontSize: "12px", color: "text.disabled" }}>
                  {isRunning ? "Starting…" : "No activity recorded."}
                </Typography>
              )}
            </Box>
          </Collapse>

          {/* Footer link to the full child session */}
          {childHref && (
            <Box
              sx={{
                px: 1.5,
                py: 0.6,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <a
                href={childHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ textDecoration: "none" }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Open full session
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Typography>
              </a>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
